import { ReactNode, useState, Suspense, lazy, useEffect, useMemo, createContext, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut } from "lucide-react";
import CookieBanner from "@/components/CookieBanner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import logoImage from "@/assets/logo-optimized.webp";
import siegelImage from "@/assets/siegel-geprueft.png";
import stripePaymentLogo from "@/assets/stripe-payment.png";
import { prefetchRoute, prefetchCriticalRoutes } from "@/lib/routePrefetch";
import { AdBanner } from "@/components/AdBanner";

// Lazy load ChatBot - it's not critical for initial render
const ChatBot = lazy(() => import("@/components/ChatBot").then((m) => ({ default: m.ChatBot })));

/**
 * Stellt sicher, dass Header/Footer beim Seitenwechsel nicht doppelt gerendert
 * werden. Wird `<Layout>` innerhalb eines bereits gerenderten Layouts verwendet
 * (App-Level Persistent Shell), fällt das innere `<Layout>` automatisch auf
 * einen Passthrough zurück.
 */
const LayoutNestingContext = createContext(false);

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({
  children
}: LayoutProps) {
  const isNested = useContext(LayoutNestingContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Derive current page name for ad targeting
  const currentPage = useMemo(() => {
    const path = location.pathname.replace(/^\//, '') || 'index';
    return path.split('/')[0];
  }, [location.pathname]);
  // Prefetch critical routes on mount for faster navigation
  useEffect(() => {
    prefetchCriticalRoutes();
  }, []);

  // Wenn das Layout bereits weiter oben im Baum (App-Shell) gerendert wurde,
  // verhalten wir uns als reiner Passthrough – verhindert doppelte Header/Footer
  // und das Aufblitzen beim Routenwechsel.
  if (isNested) {
    return <>{children}</>;
  }

  const navigation = [{
    name: 'Vorteile',
    href: "/#vorteile",
    isAnchor: true
  }, {
    name: 'Preise',
    href: "/#preise",
    isAnchor: true
  }, {
    name: 'Demo',
    href: "/#demo",
    isAnchor: true
  }, {
    name: 'Speisekarte hochladen',
    href: "/speisekarte-hochladen",
    isAnchor: false
  }];
  const isActive = (href: string) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && location.hash === href.substring(1);
    }
    return location.pathname === href;
  };
  const handleAnchorClick = (href: string, e: React.MouseEvent) => {
    if (href.startsWith('/#') && location.pathname !== '/') {
      window.location.href = href;
      e.preventDefault();
    }
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };
  return <div className="min-h-screen bg-background">
      <LayoutNestingContext.Provider value={true}>
      {/* Skip Link for Keyboard Navigation - WCAG 2.4.1 / BITV 2.0 */}
      <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-20 focus:left-4 focus:z-[60] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg">

        Zum Hauptinhalt springen
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-primary/30 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" onClick={() => {
            // Scroll to top when clicking logo
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }}>
              <img alt="SpeisekartenCheck Logo" className="w-20 h-20 rounded-lg -my-2" width={80} height={80} fetchPriority="high" src="/lovable-uploads/a9fb5a9a-c695-4953-b7d4-789adff7f287.png" />
              <span className="text-xl font-display font-semibold text-foreground">SpeisekartenCheck</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center justify-center flex-1 space-x-1 xl:space-x-2" aria-label="Hauptnavigation">
              {navigation.map((item, index) => <Link
              key={item.name}
              to={item.href}
              onClick={(e) => handleAnchorClick(item.href, e)}
              onMouseEnter={() => !item.isAnchor && prefetchRoute(item.href)}>

                  <Button variant={isActive(item.href) ? "selected" : "outline"} className={`font-medium px-6 py-2 ${index === 2 ? 'mr-8' : ''}`}>
                    {item.name}
                  </Button>
                </Link>)}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center space-x-3">
              <Link
              to={isLoggedIn ? "/profil" : "/auth"}
              onClick={() => window.scrollTo(0, 0)}
              onMouseEnter={() => prefetchRoute(isLoggedIn ? "/profil" : "/auth")}>

                <Button variant="outline" size="sm" className="px-4 py-2">
                  <User className="w-4 h-4 mr-2" />
                  {isLoggedIn ? "Mein Profil" : "Einloggen"}
                </Button>
              </Link>
              {isLoggedIn && <Button variant="outline" size="sm" onClick={handleLogout} className="p-2" title="Abmelden">
                  <LogOut className="w-4 h-4" />
                </Button>}
            </div>

            {/* Mobile menu button - WCAG 4.1.2 / BITV 2.0 */}
            <button
            className="lg:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? "Hauptmenü schließen" : "Hauptmenü öffnen"}>

              {mobileMenuOpen ? <X className="h-6 w-6 text-foreground" aria-hidden="true" /> : <Menu className="h-6 w-6 text-foreground" aria-hidden="true" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && <nav id="mobile-menu" className="lg:hidden" aria-label="Mobile Navigation">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-background border-t border-primary/30">
                {navigation.map((item) => <Link key={item.name} to={item.href} onClick={(e) => {
              handleAnchorClick(item.href, e);
              setMobileMenuOpen(false);
            }} className={`block px-3 py-2 text-base font-medium transition-colors hover:text-primary rounded ${isActive(item.href) ? 'text-primary bg-primary/10' : 'text-foreground'}`}>
                    {item.name}
                  </Link>)}
                <div className="pt-4 space-y-2">
                  <Link to={isLoggedIn ? "/profil" : "/auth"} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      <User className="w-4 h-4 mr-2" />
                      {isLoggedIn ? "Mein Profil" : "Einloggen"}
                    </Button>
                  </Link>
                  {isLoggedIn && <Button variant="outline" className="w-full" onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Abmelden
                    </Button>}
                </div>
              </div>
            </nav>}
        </div>
      </header>

      {/* Main Content - WCAG 1.3.1, 2.4.1 / BITV 2.0 */}
      <main id="main-content" role="main" tabIndex={-1} className="focus:outline-none">{children}</main>

      {/* Sidebar Ads - fixed overlay, does not affect layout */}
      {currentPage !== 'admin' && currentPage !== 'profil' &&
    <div className="hidden xl:block fixed right-4 top-24 z-40 w-[300px]">
          <AdBanner placement="sidebar_right" page={currentPage} />
        </div>
    }

      {/* Footer */}
      <footer className="bg-background border-t border-primary/30 mt-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 pt-10 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr] gap-8 md:gap-12 mb-10 justify-items-center md:justify-items-start lg:justify-items-stretch">
            {/* Company Info & Logo */}
            <div className="space-y-8 text-center md:text-left">
              <Link to="/" className="flex items-center space-x-3" onClick={() => {
              // Scroll to top when clicking logo in footer
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }}>
                <img src={siegelImage} alt="SpeisekartenCheck Prüfsiegel" className="w-20 h-20 -rotate-12" loading="lazy" />
                <span className="text-xl font-bold text-foreground">SpeisekartenCheck</span>
              </Link>
              <p className="text-base text-muted-foreground">Ihr Partner für rechtskonforme Speisen- und Getränkekarten. Automatische Prüfung in wenigen Minuten.</p>
              
              {/* Verfügbarkeit */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  {/* EU Flagge - ZUERST */}
                  <div className="w-8 h-6 bg-blue-600 relative flex items-center justify-center rounded-sm border border-muted shadow-sm">
                    <div className="absolute inset-0 flex items-center justify-center">
                      {/* 12 Sterne im Kreis */}
                      <div className="relative w-4 h-4">
                        {[...Array(12)].map((_, i) => {
                        const angle = i * 30 * Math.PI / 180;
                        const radius = 6;
                        const x = Math.cos(angle) * radius;
                        const y = Math.sin(angle) * radius;
                        return <div key={i} className="absolute w-0.5 h-0.5 bg-yellow-400" style={{
                          left: `calc(50% + ${x}px - 1px)`,
                          top: `calc(50% + ${y}px - 1px)`,
                          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                        }} />;
                      })}
                      </div>
                    </div>
                  </div>
                  {/* Deutsche Flagge - DANACH */}
                  <div className="flex flex-col overflow-hidden rounded-sm border border-muted shadow-sm">
                    <div className="w-8 h-2 bg-black"></div>
                    <div className="w-8 h-2 bg-red-600"></div>
                    <div className="w-8 h-2 bg-yellow-400"></div>
                  </div>
                  {/* Text daneben */}
                  <p className="text-sm text-foreground font-medium">Verfügbar für Deutschland</p>
                </div>
              </div>
            </div>

            {/* Infos */}
            <div className="space-y-6 text-center md:text-left lg:mx-auto">
              <h3 className="font-bold text-foreground text-xl">Infos</h3>
              <ul className="space-y-4 text-base">
                <li><Link to="/preise" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/preise')} className="text-muted-foreground hover:text-primary transition-colors">Preise</Link></li>
                <li><Link to="/demo" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/demo')} className="text-muted-foreground hover:text-primary transition-colors">Demo</Link></li>
                <li><Link to="/vorteile" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/vorteile')} className="text-muted-foreground hover:text-primary transition-colors">Ihre Vorteile mit SpeisekartenCheck</Link></li>
                <li><Link to="/kennzeichnung" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/kennzeichnung')} className="text-muted-foreground hover:text-primary transition-colors">Alles rund um die Kennzeichnung</Link></li>
              </ul>
            </div>

            {/* Wissen & Hilfe */}
            <div className="space-y-6 text-center md:text-left lg:mx-auto">
              <h3 className="font-bold text-foreground text-xl">Wissen & Hilfe</h3>
              <ul className="space-y-4 text-base">
                <li><Link to="/faq" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/faq')} className="text-muted-foreground hover:text-primary transition-colors">Häufige Fragen (FAQ)</Link></li>
                
                <li><Link to="/about" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/about')} className="text-muted-foreground hover:text-primary transition-colors">Über uns</Link></li>
              <li><Link to="/kontakt" onClick={() => window.scrollTo(0, 0)} onMouseEnter={() => prefetchRoute('/kontakt')} className="text-muted-foreground hover:text-primary transition-colors">Kontakt</Link></li>
              </ul>
              {/* Payment Provider Logos - unter Kontakt */}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <a href="https://www.stripe.com/de" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#635BFF] text-white px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity" title="Sichere Zahlung mit Stripe">
                  <span className="font-bold text-xs">stripe</span>
                </a>
                <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#003087] text-white px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity" title="Sichere Zahlung mit PayPal">
                  <span className="font-bold text-xs">Pay</span><span className="font-bold text-xs text-[#009cde]">Pal</span>
                </a>
                <a href="https://www.klarna.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#FFB3C7] text-[#0A0B09] px-3 py-1.5 rounded-md hover:opacity-80 transition-opacity" title="Sichere Zahlung mit Klarna">
                  <span className="font-bold text-xs">Klarna.</span>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-primary/30 pt-4 pb-1">
            <div className="flex flex-col sm:flex-row items-center justify-between text-xs">
              {/* Left: Copyright & Made in Germany */}
              <div className="flex items-center gap-4 text-muted-foreground">
                <span>© {new Date().getFullYear()} SpeisekartenCheck</span>
                <span className="flex items-center">
                  <span className="text-red-500 mr-1">♥</span>
                  Made in Germany
                </span>
              </div>
              
              {/* Right: Legal Links */}
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/impressum" onClick={() => window.scrollTo(0, 0)} className="text-muted-foreground hover:text-primary transition-colors">
                  Impressum
                </Link>
                <Link to="/agb" onClick={() => window.scrollTo(0, 0)} className="text-muted-foreground hover:text-primary transition-colors">
                  AGB
                </Link>
                <Link to="/datenschutz" onClick={() => window.scrollTo(0, 0)} className="text-muted-foreground hover:text-primary transition-colors">
                  Datenschutz
                </Link>
                <Link to="/widerruf" onClick={() => window.scrollTo(0, 0)} className="text-muted-foreground hover:text-primary transition-colors">
                  Widerruf
                </Link>
                <Link to="/barrierefreiheit" onClick={() => window.scrollTo(0, 0)} className="text-muted-foreground hover:text-primary transition-colors">
                  Barrierefreiheit
                </Link>
                <Link to="/cookies" onClick={() => window.scrollTo(0, 0)} className="text-muted-foreground hover:text-primary transition-colors">
                  Cookie-Verwaltung
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Cookie Banner */}
      <CookieBanner />
      
      {/* ChatBot - lazy loaded */}
      <Suspense fallback={null}>
        <ChatBot />
      </Suspense>
      </LayoutNestingContext.Provider>
    </div>;
}